import React, {useState} from 'react';
import cx from 'classnames';

import {ModalCloseButton} from '../CloseButton/ModalCloseButton.view';
import type {ClickEventHandler} from '../../../types/events';
import {useFocusFromOpened} from '../../../hooks/useFocusFromOpened';
import {useModal} from '../../..';

import styles from './Sheet.module.scss';

const handleClick: ClickEventHandler = e => e.stopPropagation();

export const Sheet: React.FC<
    React.ComponentProps<'div'> & {
        isFullWidth?: boolean;
        innerFullHeight?: boolean;
        noMargin?: boolean;
        closeButtonAriaLabel?: string;
        isOpen: boolean;
        onClose: () => void;
        withCloseButton?: boolean;
        withMask?: boolean;
        withShadow?: boolean;
    } & (
            | {heightPortrait: number; heightLandscape: number}
            | {heightPortrait?: undefined; heightLandscape?: undefined}
        )
> = ({
    isFullWidth = false,
    noMargin = false,
    isOpen = false,
    innerFullHeight = false,
    withMask = true,
    withShadow = false,
    closeButtonAriaLabel = 'Close sheet',
    onClose,
    children,
    withCloseButton = false,
    className,
    heightPortrait,
    heightLandscape,
}) => {
    const {ref} = useModal(isOpen);

    const [containerElement, setContainerElement] =
        useState<HTMLDivElement | null>(null);
    useFocusFromOpened(containerElement, isOpen);

    const sheetStyle =
        heightPortrait && heightLandscape
            ? ({
                  '--sheet-height-portrait': `${heightPortrait}dvh`,
                  '--sheet-height-landscape': `${heightLandscape}dvh`,
              } as React.CSSProperties)
            : undefined;

    return (
        <div
            ref={ref}
            className={cx(styles.wrapper, {
                [styles.open]: isOpen,
                [styles.withMask]: withMask,
            })}
            role="dialog"
        >
            {/** biome-ignore lint/a11y/noStaticElementInteractions: currently we are using it incorrectly but following the guidelines will slightly change the behaviour so we want to make change in a separate PR */}
            <div
                className={cx(styles.sheet, className, {
                    [styles.fullWidth]: isFullWidth,
                    [styles.heightPortrait]: !!heightPortrait,
                    [styles.heightLandscape]: !!heightLandscape,
                    [styles.withShadow]: withShadow,
                })}
                style={sheetStyle}
                onClick={handleClick}
                onKeyDown={handleClick}
                role="presentation"
            >
                <div
                    className={cx(styles.sheetInner, {
                        [styles.noMargin]: noMargin,
                        [styles.innerFullHeight]: innerFullHeight,
                    })}
                    ref={setContainerElement}
                    tabIndex={-1}
                >
                    {children}
                    {withCloseButton && (
                        <ModalCloseButton
                            aria-label={closeButtonAriaLabel}
                            onClose={onClose}
                            className={styles.closeButton}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
